import * as React from "react"

import { cn } from "@/lib/cn"
import { formatLocaleNumber } from "@/lib/localeFormat"

/** WebKit/Blink: 캘린더 아이콘 히트 영역을 input 전체로 — 원시 input에도 동일 클래스를 붙이면 됨 */
export const nativePickerInputClassName =
  "[&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"

/** date·datetime-local 등: input 안 어디를 눌러도 달력/시계 picker 열기 (Firefox 등 showPicker, WebKit은 위 클래스 병행) */
export function triggerNativePicker(el: HTMLInputElement): boolean {
  if (typeof el.showPicker === "function") {
    try {
      el.showPicker()
      return true
    } catch {
      /* 보안 컨텍스트·미지원 */
    }
  }
  return false
}

export function triggerNativePickerOnClick(e: React.MouseEvent<HTMLInputElement>): void {
  triggerNativePicker(e.currentTarget)
}

export function triggerNativePickerOnPointerDown(e: React.PointerEvent<HTMLInputElement>): void {
  triggerNativePicker(e.currentTarget)
}

function isNativePickerInputType(
  t: React.HTMLInputTypeAttribute | undefined,
  multiline: boolean | undefined,
  isPrice: boolean
): boolean {
  if (multiline || isPrice) return false
  return (
    t === "date" ||
    t === "datetime-local" ||
    t === "month" ||
    t === "week" ||
    t === "time"
  )
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  suffix?: React.ReactNode;
  multiline?: boolean;
  rows?: number;
  wrapperClassName?: string;
}

/** type="price" 시 표시용 천 단위 포맷 (콤마) */
function formatPriceDisplay(value: string | number | readonly string[] | undefined): string {
  if (value === '' || value == null || Array.isArray(value)) return '';
  const raw = String(value).replace(/,/g, '');
  if (raw === '') return '';
  const num = parseFloat(raw);
  if (Number.isNaN(num)) return raw;
  return formatLocaleNumber(num);
}

const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  ({ className, type, label, error, icon, suffix, multiline, rows, wrapperClassName, value, onChange, onClick, onPointerDown, ...props }, ref) => {
    const pickerOpenedFromPointerRef = React.useRef(false)

    // Checkbox/Radio 타입 체크
    const isCheckboxOrRadio = type === 'checkbox' || type === 'radio';

    // type="price": 천 단위 콤마 표시, onChange 시 콤마 제거 후 전달
    const isPrice = type === 'price';
    const priceDisplayValue = isPrice ? formatPriceDisplay(value) : undefined;
    const handlePriceChange = isPrice && onChange
      ? (e: React.ChangeEvent<HTMLInputElement>) => {
          const raw = String(e.target.value).replace(/,/g, '');
          (onChange as React.ChangeEventHandler<HTMLInputElement>)({
            ...e,
            target: { ...e.target, value: raw },
          } as React.ChangeEvent<HTMLInputElement>);
        }
      : undefined;

    const openPickerOnClick = isNativePickerInputType(type, multiline, isPrice)

    const mergedDatePickerClick =
      openPickerOnClick
        ? (e: React.MouseEvent<HTMLInputElement>) => {
            onClick?.(e)
            if (e.defaultPrevented) return
            if (pickerOpenedFromPointerRef.current) {
              pickerOpenedFromPointerRef.current = false
              return
            }
            triggerNativePickerOnClick(e)
          }
        : onClick

    const mergedDatePickerPointerDown =
      openPickerOnClick
        ? (e: React.PointerEvent<HTMLInputElement>) => {
            onPointerDown?.(e)
            if (e.defaultPrevented) return
            pickerOpenedFromPointerRef.current = triggerNativePicker(e.currentTarget)
          }
        : onPointerDown

    // 일반 input/textarea 스타일
    const commonClasses = cn(
      "flex w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background transition-all",
      "placeholder:text-muted-foreground",
      "hover:border-brand-500",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20 focus-visible:ring-offset-0 focus-visible:border-brand-500",
      "disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
      error && "border-red-500 focus-visible:ring-red-500/20 focus-visible:border-red-500",
      icon && "pl-10",
      suffix && "pr-6",
      (type === 'number' || isPrice) && "[&::-webkit-inner-spin-button]:ml-1 [&::-webkit-outer-spin-button]:ml-1",
      openPickerOnClick && "relative",
      openPickerOnClick && "cursor-pointer",
      openPickerOnClick && nativePickerInputClassName,
      className
    )

    // Checkbox/Radio 스타일
    const checkboxRadioClasses = cn(
      "h-4 w-4 rounded border-gray-300 text-blue-600 transition-colors",
      "focus:ring-2 focus:ring-blue-500 focus:ring-offset-0",
      "disabled:cursor-not-allowed disabled:opacity-50",
      type === 'checkbox' && "rounded",
      type === 'radio' && "rounded-full",
      error && "border-red-500 focus:ring-red-500",
      className
    )

    // Checkbox/Radio 레이아웃 (checked, onChange 명시 전달 — destructure 시 누락 방지)
    if (isCheckboxOrRadio) {
      return (
        <div className={cn("flex items-center", wrapperClassName)}>
          <input
            type={type}
            className={checkboxRadioClasses}
            ref={ref as React.Ref<HTMLInputElement>}
            {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
            checked={props.checked}
            onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
          />
          {label && (
            <label className="ml-2 text-sm font-medium text-gray-700 cursor-pointer">
              {label}
            </label>
          )}
          {error && (
            <p className="ml-2 text-sm text-red-500 animate-in fade-in slide-in-from-top-1">
              {error}
            </p>
          )}
        </div>
      )
    }

    // 일반 input/textarea 레이아웃 (multiline 시 value/onChange를 명시 전달 — destructure로 빠지지 않도록)
    const inputElement = multiline ? (
      <textarea
        className={cn("min-h-20", commonClasses)}
        ref={ref as React.Ref<HTMLTextAreaElement>}
        rows={rows}
        value={value}
        onChange={onChange as React.ChangeEventHandler<HTMLTextAreaElement>}
        {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
      />
    ) : (
      <input
        type={isPrice ? 'text' : type}
        inputMode={isPrice ? 'numeric' : undefined}
        className={cn("h-[38px]", commonClasses)}
        ref={ref as React.Ref<HTMLInputElement>}
        value={priceDisplayValue !== undefined ? priceDisplayValue : value}
        onChange={handlePriceChange ?? onChange}
        onPointerDown={mergedDatePickerPointerDown}
        onClick={mergedDatePickerClick}
        {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
      />
    )

    return (
      <div className={cn("w-full", wrapperClassName)}>
        {label && (
          <label className="block text-sm font-medium text-gray-600 ml-0.5">
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {icon}
            </div>
          )}
          {inputElement}
          {suffix && (
            <div className={cn(
              "absolute top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none transition-all",
              type === 'number' ? "right-3" : "right-3"
            )}>
              {suffix}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1 text-sm font-medium text-red-500 ml-0.5 animate-in fade-in slide-in-from-top-1">
            {error}
          </p>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
